import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { ArrowLeft, ArrowRight, Zap, Microscope } from 'lucide-react';
import SporeField from './SporeField';
import { useContent } from '../context/ContentContext';
import { imgUrl, imgSrcSet } from '../lib/api';

const HERO_IMG = 'https://images.unsplash.com/photo-1540258521336-5c1489b392e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxtdXNocm9vbSUyMG15Y2VsaXVtJTIwbGFib3JhdG9yeSUyMGRhcmslMjBuZW9uJTIwYmlvdGVjaHxlbnwwfHx8fDE3ODg5NzkwNzZ8MA&ixlib=rb-4.1.0&q=85';

const IMG_BIO = 'https://images.unsplash.com/photo-1538836026403-e143e8a59f04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxiaW9sdW1pbmVzY2VudCUyMG11c2hyb29tJTIwZ2xvdyUyMGRhcmslMjBsYWJ8ZW58MHx8fHwxNzg5MDM1NTQ1fDA&ixlib=rb-4.1.0&q=85';
const IMG_EXOTIC = 'https://images.unsplash.com/photo-1762186540732-5a5d66479ccf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHxfHxleG90aWMlMjB3aWxkJTIwbXVzaHJvb21zJTIwbWFjcm8lMjBmb3Jlc3QlMjBkYXJrfGVufDB8fHx8MTc4OTAzNTU0OXww&ixlib=rb-4.1.0&q=85';
const IMG_COLONY = 'https://images.unsplash.com/photo-1762186540717-7d66b9f826d5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHxleG90aWMlMjB3aWxkJTIwbXVzaHJvb21zJTIwbWFjcm8lMjBmb3Jlc3QlMjBkYXJrfGVufDB8fHx8MTc4OTAzNTU0OXww&ixlib=rb-4.1.0&q=85';

export const HERO_DEFAULTS = {
  eyebrow: 'terrafungi.fr — myciculture de précision',
  lines: [
    { text: 'CULTIVEZ', accent: false },
    { text: "L'INVISIBLE", accent: true },
    { text: 'RÉCOLTEZ', accent: false },
    { text: "L'EXTRAORDINAIRE", accent: true },
  ],
  subtext: "Mycélium d'élite, substrats stériles et équipement de laboratoire. Chaque souche est sélectionnée, chaque sac est stérilisé à 121 °C, chaque récolte est une expérience réussie.",
  cta1: 'Explorer le catalogue',
  cta2: 'Le manifeste',
  image: HERO_IMG,
  stats: [
    { k: 'VARIÉTÉS ACTIVES', v: '12+' },
    { k: 'STÉRILITÉ PROTOCOLE', v: '99,7 %' },
    { k: 'EXPÉDITION', v: '48 H' },
    { k: 'SALLE BLANCHE', v: 'ISO-5' },
  ],
  slides: [
    {
      badge: 'SOUCHE F3 · BEST-SELLER',
      title1: 'PLEUROTE',
      title2: 'ROSE FLAMINGO',
      desc: 'Le sprinteur du laboratoire. Colonisation en 7 jours, bouquets rose vif, rendement record — la variété qui transforme les débutants en producteurs.',
      strain: 'PS-DJM-01',
      stats: [{ k: 'COLONISATION', v: 'J+7' }, { k: 'RENDEMENT', v: '250 % BE' }],
      image: HERO_IMG,
      accent: 'neon',
      cta: 'Voir la souche',
      cta_link: '#catalogue',
    },
    {
      badge: 'GÉNÉTIQUE RARE',
      title1: "LION'S MANE",
      title2: 'NOOTROPIQUE',
      desc: "Hericium erinaceus, l'icicle blanche des biohackers. Mycélium vigoureux, fructifications spectaculaires, réputation légendaire.",
      strain: 'HE-ERI-07',
      stats: [{ k: 'HUMIDITÉ', v: '85–95 %' }, { k: 'FRUCTI.', v: '16–21 °C' }],
      image: IMG_COLONY,
      accent: 'cyber',
      cta: 'Explorer la génétique',
      cta_link: '#catalogue',
    },
    {
      badge: 'LÉGENDE UMAMI',
      title1: 'SHIITAKE',
      title2: 'SOUCHE 3782',
      desc: "Lentinula edodes haute performance sur sciure de chêne. Chair dense, umami profond, jusqu'à 4 flushes par bloc de 3 kg.",
      strain: 'LE-3782-F3',
      stats: [{ k: 'FLUSHES', v: '3–4' }, { k: 'BLOC', v: '3 KG' }],
      image: IMG_EXOTIC,
      accent: 'violet',
      cta: 'Voir le kit shiitake',
      cta_link: '#catalogue',
    },
    {
      badge: 'ÉQUIPEMENT PRO',
      title1: 'SALLE BLANCHE',
      title2: 'ISO-5 PORTABLE',
      desc: 'Still Air Box, sacs filtrants 0,2 µ, agar MEA pré-dosé : tout le protocole du laboratoire, livré chez vous en 48 h.',
      strain: 'LAB-EQP-X',
      stats: [{ k: 'STÉRILITÉ', v: '99,7 %' }, { k: 'EXPÉDITION', v: '48 H' }],
      image: IMG_BIO,
      accent: 'neon',
      cta: 'Équiper mon labo',
      cta_link: '#catalogue',
    },
  ],
};

const ACCENTS = {
  neon: { text: 'text-neon', chip: 'border-neon/40 text-neon', bar: 'bg-neon', dot: 'bg-neon' },
  cyber: { text: 'text-cyber', chip: 'border-cyber/40 text-cyber', bar: 'bg-cyber', dot: 'bg-cyber' },
  violet: { text: 'text-bioviolet', chip: 'border-bioviolet/40 text-bioviolet', bar: 'bg-bioviolet', dot: 'bg-bioviolet' },
};

const Hero = () => {
  const c = useContent('hero', HERO_DEFAULTS);
  const heroWidth = typeof window !== 'undefined' && window.innerWidth < 768 ? 768 : 1440;
  const slides = (c.slides && c.slides.length)
    ? c.slides
    : [{
        badge: c.eyebrow, title1: c.lines?.[0]?.text || 'CULTIVEZ',
        title2: c.lines?.[1]?.text || "L'INVISIBLE", desc: c.subtext, strain: 'TF-LAB-00',
        stats: (c.stats || []).slice(0, 2), image: c.image, accent: 'neon',
        cta: c.cta1, cta_link: '#catalogue',
      }];
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '14%']);

  const go = useCallback((d) => setIndex((i) => (i + d + slides.length) % slides.length), [slides.length]);

  useEffect(() => {
    if (paused || slides.length < 2) return undefined;
    const t = setTimeout(() => go(1), 6000);
    return () => clearTimeout(t);
  }, [index, paused, go, slides.length]);

  const s = slides[Math.min(index, slides.length - 1)];
  const a = ACCENTS[s.accent] || ACCENTS.neon;

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setMouse({ x: ((e.clientX - r.left) / r.width - 0.5) * 2, y: ((e.clientY - r.top) / r.height - 0.5) * 2 });
      }}
      className="relative h-[100dvh] min-h-[620px] overflow-hidden bg-void"
    >
      <AnimatePresence initial={false}>
        <motion.div
          key={index}
          className="absolute inset-0"
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.03 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        >
          {s.video ? (
            <motion.video
              src={imgUrl(s.video)}
              poster={s.image ? imgUrl(s.image) : undefined}
              autoPlay
              muted
              loop
              playsInline
              style={{ y: bgY, x: mouse.x * -16 }}
              className="h-[112%] w-full object-cover"
              data-testid="hero-slide-video"
            />
          ) : (
            <motion.img
              srcSet={imgSrcSet(s.image)}
              sizes="100vw"
              src={imgUrl(s.image, heroWidth)}
              fetchPriority="high"
              decoding="async"
              alt={s.title1}
              style={{ y: bgY, x: mouse.x * -16 }}
              className="h-[112%] w-full object-cover"
            />
          )}
          <div className="hero-shade absolute inset-0" />
          <div className="hero-shade-side absolute inset-0" />
        </motion.div>
      </AnimatePresence>
      <div className="pointer-events-none absolute inset-0 z-[5] opacity-50">
        <SporeField />
      </div>

      <motion.div
        className="relative z-10 mx-auto flex h-full max-w-7xl cursor-grab flex-col justify-center px-4 pb-28 pt-24 active:cursor-grabbing sm:px-6 lg:px-8"
        drag={slides.length > 1 ? 'x' : false}
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.12}
        onDragEnd={(e, info) => {
          if (info.offset.x < -80 || info.velocity.x < -400) go(1);
          else if (info.offset.x > 80 || info.velocity.x > 400) go(-1);
        }}
        data-testid="hero-slider-container"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial="hidden"
            animate="show"
            exit={{ opacity: 0, y: -24, transition: { duration: 0.3 } }}
            variants={{ show: { transition: { staggerChildren: 0.1 } } }}
            className="max-w-3xl"
          >
            <motion.p
              variants={{ hidden: { opacity: 0, x: -24 }, show: { opacity: 1, x: 0, transition: { duration: 0.6 } } }}
              className={`mb-6 inline-flex items-center gap-3 border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.3em] ${a.chip} bg-void/60 backdrop-blur`}
            >
              <span className={`inline-block h-1.5 w-1.5 rounded-full ${a.dot}`} style={{ animation: 'tf-pulse-glow 1.6s ease-in-out infinite' }} />
              {s.badge}
            </motion.p>

            <h1 className="font-display text-4xl font-extrabold uppercase leading-[0.98] tracking-[-0.02em] sm:text-5xl lg:text-6xl" data-testid="hero-slider-title">
              {[s.title1, s.title2].map((t, i) => (
                <span key={t} className="block overflow-hidden">
                  <motion.span
                    className={`block ${i === 1 ? `${a.text} text-glow` : 'text-slate-100'}`}
                    variants={{ hidden: { y: '115%' }, show: { y: 0, transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] } } }}
                  >
                    {t}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.7 } } }}
              className="mt-6 max-w-md text-sm leading-relaxed text-slate-300 sm:text-base"
              data-testid="hero-subtext"
            >
              {s.desc}
            </motion.p>

            <motion.div
              variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.7 } } }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <a
                href={s.cta_link || '#catalogue'}
                data-testid="hero-slider-cta-button"
                className="clip-frame-sm bg-neon px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.25em] text-void transition-colors hover:bg-cyber"
              >
                <span className="flex items-center gap-2"><Zap size={14} /> {s.cta}</span>
              </a>
              <a
                href="#manifeste"
                data-testid="hero-cta-manifesto"
                className="clip-frame-sm border border-white/20 bg-void/40 px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-slate-200 backdrop-blur transition-colors hover:border-neon/60 hover:text-neon"
              >
                <span className="flex items-center gap-2"><Microscope size={14} /> {c.cta2}</span>
              </a>
              <span className="hidden font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 sm:block" data-testid="hero-strain-code">
                STRAIN // {s.strain}
              </span>
            </motion.div>
          </motion.div>
        </AnimatePresence>

        <div className="pointer-events-none absolute right-8 top-1/2 z-10 hidden -translate-y-1/2 flex-col gap-3 lg:flex" style={{ transform: `translate(${mouse.x * 10}px, calc(-50% + ${mouse.y * 8}px))` }}>
          {(s.stats || []).map((st, i) => (
            <motion.div
              key={`${index}-${st.k}`}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 + i * 0.15, duration: 0.7 }}
              className="clip-frame-sm glass-panel border border-white/10 px-5 py-4 text-right"
            >
              <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">{st.k}</p>
              <p className={`font-display text-2xl font-extrabold ${a.text}`}>{st.v}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {slides.length > 1 && (
        <div className="absolute inset-x-0 bottom-0 z-20 border-t border-white/8 bg-void/70 backdrop-blur-md">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-1 font-mono text-xs text-slate-400" data-testid="hero-slider-counter">
              <AnimatePresence mode="popLayout">
                <motion.span
                  key={index}
                  initial={{ y: 14, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -14, opacity: 0 }}
                  transition={{ duration: 0.35 }}
                  className={`inline-block font-bold ${a.text}`}
                >
                  {String(index + 1).padStart(2, '0')}
                </motion.span>
              </AnimatePresence>
              <span className="text-slate-600">/ {String(slides.length).padStart(2, '0')}</span>
            </div>

            <div className="flex flex-1 items-center justify-center gap-1.5 px-4">
              {slides.map((sl, i) => (
                <button
                  key={sl.strain || i}
                  onClick={() => setIndex(i)}
                  data-testid={i === index ? 'hero-slider-progress-indicator' : `hero-slider-segment-${i}`}
                  aria-label={`Slide ${i + 1}`}
                  className="group relative h-4 flex-1 max-w-24"
                >
                  <span className="absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2 bg-white/10 transition-colors group-hover:bg-white/25" />
                  {i < index && <span className="absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2 bg-neon/50" />}
                  {i === index && (
                    <span
                      key={`fill-${index}-${paused}`}
                      className={`progress-fill absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2 ${a.bar}`}
                      style={{ animationPlayState: paused ? 'paused' : 'running' }}
                    />
                  )}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => go(-1)}
                data-testid="hero-slider-prev-button"
                aria-label="Slide précédente"
                className="clip-frame-sm flex h-10 w-10 items-center justify-center border border-white/15 text-slate-300 transition-all hover:border-neon hover:text-neon hover:shadow-[0_0_15px_rgba(16,185,129,0.3)]"
              >
                <ArrowLeft size={16} />
              </button>
              <button
                onClick={() => go(1)}
                data-testid="hero-slider-next-button"
                aria-label="Slide suivante"
                className="clip-frame-sm flex h-10 w-10 items-center justify-center border border-white/15 text-slate-300 transition-all hover:border-neon hover:text-neon hover:shadow-[0_0_15px_rgba(16,185,129,0.3)]"
              >
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;
